/**
 * TaskResultCards: renders the tasks a chat reply is about (from a `list_tasks` tool result) as clickable cards instead of a wall
 * of text. Clicking a card fires `lyra:edit-task`; the Home page listens and opens the edit-task modal in place.
 */
import React from 'react';
import { Calendar, Clock, Flag } from 'lucide-react';

const PRIORITY = {
  High:   { fg: 'var(--lyra-color-danger)',  bg: 'var(--lyra-color-danger-bg)' },
  Medium: { fg: 'var(--lyra-color-warning)', bg: 'var(--lyra-color-warning-bg)' },
  Low:    { fg: 'var(--lyra-color-success)', bg: 'var(--lyra-color-success-bg)' },
};
const STATUS = {
  Todo:       { label: 'To do',       fg: 'var(--app-text-2)',          bg: 'var(--app-surface-3)' },
  InProgress: { label: 'In progress', fg: 'var(--app-primary-deep)',    bg: 'var(--app-primary-soft)' },
  Done:       { label: 'Done',        fg: 'var(--lyra-color-success)',  bg: 'var(--lyra-color-success-bg)' },
};

/** Pull task-shaped objects (id + title + status/priority) out of any tool result: JSON text, MCP content[], or nested tasks/items/data. */
function collectTasks(node, out, depth = 0) {
  if (depth > 4 || node == null) return;
  if (typeof node === 'string') {
    const t = node.trim();
    if (t[0] === '[' || t[0] === '{') { try { collectTasks(JSON.parse(t), out, depth + 1); } catch { /* not JSON */ } }
    return;
  }
  if (Array.isArray(node)) { node.forEach((n) => collectTasks(n, out, depth + 1)); return; }
  if (typeof node !== 'object') return;
  const id = node.id || node._id;
  if (id && node.title && (node.status || node.priority)) { out.push({ ...node, id: String(id) }); return; }
  Object.values(node).forEach((v) => collectTasks(v, out, depth + 1));
}

/** Tasks from a run's tool calls (any tool with "task" in its name; the same task is listed once). */
export function tasksOf(res) {
  const out = [];
  (res?.toolCalls || []).forEach((tc) => {
    const name = tc?.skillId || tc?.name || '';
    if (!tc || tc.ok === false || tc.error || !/task/i.test(name) || /project|idea|note/i.test(name)) return;
    collectTasks(tc.result, out);
  });
  if (!out.length && res?.toolCalls?.length) console.debug('[tasks] no task cards; tool calls:', res.toolCalls.map((t) => t.skillId || t.name));
  const seen = new Set();
  return out.filter((t) => !seen.has(t.id) && seen.add(t.id));
}

/** Drop the model's own numbered/bulleted task listing from a reply, keeping the sentence(s) that introduce it. */
export function introOf(text) {
  const lines = String(text || '').split('\n');
  const i = lines.findIndex((l) => /^\s*(\d+[.)]|[-*•])\s+/.test(l));
  return (i === -1 ? lines : lines.slice(0, i)).join('\n').trim();
}

const fmtDue = (d) => {
  const t = new Date(d);
  return Number.isNaN(t.getTime()) ? null : t.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const chip = (fg, bg) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, color: fg, background: bg });

export default function TaskResultCards({ tasks }) {
  const open = (task) => window.dispatchEvent(new CustomEvent('lyra:edit-task', { detail: { taskId: task.id, task } }));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
      {tasks.map((t) => {
        const p = PRIORITY[t.priority];
        const s = STATUS[t.status] || STATUS.Todo;
        const due = t.dueDate && fmtDue(t.dueDate);
        return (
          <button key={t.id} type="button" onClick={() => open(t)} title="Open task"
            style={{ textAlign: 'left', width: '100%', cursor: 'pointer', fontFamily: 'inherit', padding: '10px 12px', borderRadius: 12, background: 'var(--lyra-color-surface)', border: '1px solid var(--lyra-color-border)', borderLeft: `3px solid ${p ? p.fg : 'var(--app-border-strong)'}`, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--lyra-color-text)', lineHeight: 1.35 }}>{t.title}</div>
            {t.description && (
              <div style={{ fontSize: 12, color: 'var(--lyra-color-text-muted)', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{t.description}</div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              <span style={chip(s.fg, s.bg)}>{s.label}</span>
              {p && <span style={chip(p.fg, p.bg)}><Flag size={10} />{t.priority}</span>}
              {due && <span style={chip('var(--app-text-2)', 'var(--app-surface-3)')}><Calendar size={10} />{due}</span>}
              {t.estimatedTime && <span style={chip('var(--app-text-2)', 'var(--app-surface-3)')}><Clock size={10} />{t.estimatedTime}</span>}
            </div>
          </button>
        );
      })}
    </div>
  );
}
